
if (!customElements.get('slideshow-component')) {
    customElements.define('slideshow-component', class SlideshowComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = this.querySelector('.slideshow');
            this.slides = this.querySelectorAll('.slideshow__slide');
            this.dots = this.querySelectorAll('.slider-counter__link--dots');
            this.prevButton = this.querySelector('.slider-button--prev');
            this.nextButton = this.querySelector('.slider-button--next');
            this.autoplayButton = this.querySelector('.slideshow__autoplay');

            this.currentSlide = 0;
            this.isTransitioning = false;
            this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
            this.autoplaySpeed = parseInt(this.slider.dataset.speed) * 1000 || 5000;
            this.autoplayTimer = null;

            this.init();
        }

        init() {
            if (this.slides.length <= 1) return;

            this.showSlide(0, false);
            this.setupEventListeners();
            if (this.autoplayEnabled) {
                this.startAutoplay();
            }
        }

        setupEventListeners() {
            this.dots.forEach((dot, index) => {
                dot.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (!this.isTransitioning && index !== this.currentSlide) {
                        this.goToSlide(index);
                    }
                });
            });

            if (this.prevButton) {
                this.prevButton.addEventListener('click', () => this.prevSlide());
            }

            if (this.nextButton) {
                this.nextButton.addEventListener('click', () => this.nextSlide());
            }

            if (this.autoplayButton) {
                this.autoplayButton.addEventListener('click', () => this.toggleAutoplay());
            }

            this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
            this.slider.addEventListener('mouseleave', () => this.resumeAutoplay());

            document.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowLeft') this.prevSlide();
                if (e.key === 'ArrowRight') this.nextSlide();
            });

            let touchStartX = 0;
            let touchEndX = 0;

            this.slider.addEventListener('touchstart', (e) => {
                touchStartX = e.changedTouches[0].screenX;
            }, { passive: true });

            this.slider.addEventListener('touchend', (e) => {
                touchEndX = e.changedTouches[0].screenX;
                this.handleSwipe(touchStartX, touchEndX);
            }, { passive: true });

            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.pauseAutoplay();
                } else if (this.autoplayEnabled && !this.autoplayButton?.classList.contains('slideshow__autoplay--paused')) {
                    this.resumeAutoplay();
                }
            });
        }

        handleSwipe(startX, endX) {
            const swipeThreshold = 50;
            const diff = startX - endX;

            if (Math.abs(diff) > swipeThreshold) {
                if (diff > 0) {
                    this.nextSlide();
                } else {
                    this.prevSlide();
                }
            }
        }

        showSlide(index, animate = true) {
            if (this.isTransitioning && animate) return;

            this.isTransitioning = true;

            this.slides.forEach((slide, i) => {
                slide.classList.remove('slideshow__slide--active');
                slide.style.zIndex = i === index ? '2' : '1';

                if (i === index) {
                    setTimeout(() => {
                        slide.classList.add('slideshow__slide--active');
                    }, 10);
                }
            });

            this.dots.forEach((dot, i) => {
                if (i === index) {
                    dot.classList.add('slider-counter__link--active');
                    dot.setAttribute('aria-current', 'true');
                } else {
                    dot.classList.remove('slider-counter__link--active');
                    dot.removeAttribute('aria-current');
                }
            });

            const counterCurrent = this.querySelector('.slider-counter--current');
            if (counterCurrent) {
                counterCurrent.textContent = index + 1;
            }

            this.currentSlide = index;

            this.slider.setAttribute('aria-live', 'polite');

            setTimeout(() => {
                this.isTransitioning = false;
            }, 1800);
        }

        goToSlide(index) {
            if (index === this.currentSlide) return;
            this.showSlide(index);
            this.resetAutoplay();
        }

        nextSlide() {
            const next = (this.currentSlide + 1) % this.slides.length;
            this.showSlide(next);
        }

        prevSlide() {
            const prev = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
            this.showSlide(prev);
        }

        startAutoplay() {
            this.autoplayTimer = setInterval(() => {
                this.nextSlide();
            }, this.autoplaySpeed);
        }

        pauseAutoplay() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        }

        resumeAutoplay() {
            if (this.autoplayEnabled && !this.autoplayButton?.classList.contains('slideshow__autoplay--paused')) {
                this.startAutoplay();
            }
        }

        resetAutoplay() {
            this.pauseAutoplay();
            this.resumeAutoplay();
        }

        toggleAutoplay() {
            if (!this.autoplayButton) return;

            this.autoplayEnabled = !this.autoplayEnabled;
            this.autoplayButton.classList.toggle('slideshow__autoplay--paused');

            if (this.autoplayEnabled) {
                this.startAutoplay();
                this.autoplayButton.setAttribute('aria-label', 'Pause slideshow');
            } else {
                this.pauseAutoplay();
                this.autoplayButton.setAttribute('aria-label', 'Play slideshow');
            }
        }

        disconnectedCallback() {
            this.pauseAutoplay();
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlideshows);
} else {
    initSlideshows();
}

function initSlideshows() {
    const slideshows = document.querySelectorAll('slideshow-component');
    slideshows.forEach(slideshow => {
        if (!slideshow.initialized) {
            slideshow.initialized = true;
        }
    });
}

if (Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => initSlideshows());
    document.addEventListener('shopify:section:select', () => initSlideshows());
}